import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Undo2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FloorplanMessage } from '@/types';

interface FloorplanMessageThreadProps {
  messages: FloorplanMessage[];
  activeMessageNumber: number | null;
  isGenerating: boolean;
  onSend: (prompt: string) => void;
  onSelectMessage: (messageNumber: number) => void;
  onRetryFailed: (messageId: string) => void;
}

export const FloorplanMessageThread: React.FC<FloorplanMessageThreadProps> = ({
  messages,
  activeMessageNumber,
  isGenerating,
  onSend,
  onSelectMessage,
  onRetryFailed,
}) => {
  const [promptText, setPromptText] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    const trimmed = promptText.trim();
    if (!trimmed || isGenerating) return;
    onSend(trimmed);
    setPromptText('');
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter sends
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col gap-2 bg-card border border-border rounded-lg p-3 min-h-0 flex-1 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <MessageSquare className="size-4 stroke-[1.5]" />
        Deine Nachrichten
      </div>

      <div ref={listRef} className="flex flex-col gap-1.5 overflow-y-auto flex-1">
        {messages.length === 0 && !isGenerating && (
          <div className="flex items-center justify-center py-8 text-center">
            <p className="text-xs text-muted-foreground px-4">
              Noch keine Nachrichten. Beschreibe unten, was geändert werden soll.
            </p>
          </div>
        )}

        {messages.map((msg) => {
          const isActive = msg.messageNumber === activeMessageNumber && msg.status === 'success';
          const isFailed = msg.status === 'failed';
          const isPending = msg.status === 'pending';

          return (
            <div
              key={msg.id}
              className={`rounded-md p-2 border-l-4 transition-colors ${
                isActive
                  ? 'bg-primary/10 border-primary'
                  : isFailed
                  ? 'bg-neutral-50 border-neutral-300 opacity-80'
                  : isPending
                  ? 'bg-neutral-50 border-neutral-300 animate-pulse'
                  : 'bg-neutral-50 border-neutral-300'
              }`}
            >
              <div className="flex items-center gap-1.5 text-[10px] mb-1">
                {isFailed && <AlertCircle className="size-3 text-neutral-500 stroke-[1.5]" />}
                {isPending && <Loader2 className="size-3 text-neutral-500 animate-spin" />}
                <span className={isActive ? 'text-primary font-bold' : 'text-muted-foreground font-semibold'}>
                  Nachricht {msg.messageNumber}
                  {isActive && ' · aktuell'}
                  {isPending && ' · wird erstellt…'}
                  {isFailed && ' · Fehler'}
                </span>
              </div>
              <p className="text-xs text-foreground leading-relaxed break-words">
                „{msg.userPrompt}"
              </p>
              {isFailed && msg.errorMessage && (
                <p className="text-[10px] text-neutral-500 mt-1 italic break-words">
                  {msg.errorMessage}
                </p>
              )}
              {msg.status === 'success' && !isActive && (
                <div className="mt-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() => onSelectMessage(msg.messageNumber)}
                  >
                    <Undo2 className="size-3 mr-1" />
                    Zu diesem Stand
                  </Button>
                </div>
              )}
              {isFailed && (
                <div className="mt-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() => onRetryFailed(msg.id)}
                  >
                    Erneut versuchen
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-1.5 mt-1">
        <textarea
          className="w-full border border-border rounded-md p-2 text-xs bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary resize-none"
          placeholder="Was soll geändert werden? Je genauer du beschreibst, desto besser das Ergebnis."
          rows={3}
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          onKeyDown={handleKey}
          disabled={isGenerating}
        />
        <Button
          onClick={handleSend}
          disabled={!promptText.trim() || isGenerating}
          className="w-full text-xs bg-primary hover:bg-primary/90 text-white"
        >
          {isGenerating ? (
            <>
              <Loader2 className="size-3 mr-1 animate-spin" />
              Wird erstellt…
            </>
          ) : (
            'Nachricht senden (kostenlos)'
          )}
        </Button>
      </div>
    </div>
  );
};
